/**
 * useDbSync — bidirectional sync between Zustand store and /api/persist.
 *
 * On mount  : loads DB snapshot → hydrates store (source of truth > localStorage)
 * On change : subscribes to store slices → debounce → writes to API
 *
 * Graceful degradation: if the API is unreachable, falls back to localStorage only.
 */

import { useEffect, useRef } from 'react';
import { useTerminalStore } from '../stores/terminal.store.js';
import { generateId, DEFAULT_UTBOT_CONFIG } from '@arc/shared';
import type { Order, PriceAlert, EquityPoint, CloseReason, Position } from '@arc/shared';
import {
  loadSnapshot,
  saveSettings,
  saveOrders,
  saveAlerts,
  saveEquityPoints,
  getPersistStatus,
  type DbOrder,
  type DbPriceAlert,
  type DbEquityPoint,
} from '../services/persistenceClient.js';

// ─── Store → DB row mappers ───────────────────────────────────────────────────

function orderToDb(o: Order): DbOrder {
  return {
    id:           o.id,
    symbol:       o.symbol,
    side:         o.side,
    type:         o.type,
    size:         o.size,
    leverage:     o.leverage,
    status:       o.status,
    fill_price:   o.fillPrice   ?? null,
    exchange:     o.exchange,
    mode:         o.mode,
    pnl:          o.pnl         ?? null,
    timestamp:    o.timestamp,
    closed_at:    o.closedAt    ?? null,
    close_reason: o.closeReason ?? null,
  };
}

function alertToDb(a: PriceAlert): DbPriceAlert {
  return {
    id:           a.id,
    symbol:       a.symbol,
    condition:    a.condition,
    target_price: a.targetPrice,
    created_at:   a.createdAt,
    fired_at:     a.firedAt ?? null,
    repeat:       a.repeat  ? 1 : 0,
    note:         a.note    ?? null,
  };
}

function equityToDb(p: EquityPoint): DbEquityPoint {
  return { timestamp: p.timestamp, equity: p.equity };
}

// ─── DB row → Store type mappers ──────────────────────────────────────────────

function dbToOrder(r: DbOrder): Order {
  // Build incrementally to satisfy exactOptionalPropertyTypes
  const o: Order = {
    id:        r.id,
    symbol:    r.symbol,
    side:      r.side     as Order['side'],
    type:      r.type     as Order['type'],
    size:      r.size,
    leverage:  r.leverage,
    status:    r.status   as Order['status'],
    exchange:  r.exchange as Order['exchange'],
    mode:      r.mode     as Order['mode'],
    timestamp: r.timestamp,
  };
  if (r.fill_price   !== null) o.fillPrice   = r.fill_price;
  if (r.pnl          !== null) o.pnl          = r.pnl;
  if (r.closed_at    !== null) o.closedAt     = r.closed_at;
  if (r.close_reason !== null) o.closeReason  = r.close_reason as CloseReason;
  return o;
}

function dbToAlert(r: DbPriceAlert): PriceAlert {
  return {
    id:          r.id,
    symbol:      r.symbol,
    condition:   r.condition as PriceAlert['condition'],
    targetPrice: r.target_price,
    createdAt:   r.created_at,
    repeat:      r.repeat === 1,
    ...(r.fired_at !== null ? { firedAt: r.fired_at } : {}),
    ...(r.note     !== null ? { note:    r.note }      : {}),
  };
}

function dbToEquity(r: DbEquityPoint): EquityPoint {
  return { timestamp: r.timestamp, equity: r.equity };
}

// ─── Debounce helper ──────────────────────────────────────────────────────────

function useDebouncedCallback(fn: () => void, delayMs: number) {
  const fnRef    = useRef(fn);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  fnRef.current  = fn;

  return useRef(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fnRef.current(), delayMs);
  }).current;
}

// ─── Settings rows ────────────────────────────────────────────────────────────

type TerminalState = ReturnType<typeof useTerminalStore.getState>;

const SETTINGS_KEYS = [
  'theme', 'layout', 'activeSymbol', 'uiScale', 'tradeMode', 'settings',
  'autoTradeConfig', 'riskSettings', 'paperAccount', 'panelLayouts',
  'autoTradeRecords', 'activePanels',
] as const;
type SettingsKey = typeof SETTINGS_KEYS[number];

/** The DB value (a string) for every synced settings key. */
function settingsRow(s: TerminalState): Record<SettingsKey, string> {
  return {
    theme:            s.theme,
    layout:           s.layout,
    activeSymbol:     s.activeSymbol,
    uiScale:          String(s.uiScale),
    tradeMode:        s.tradeMode,
    settings:         JSON.stringify(s.settings),
    autoTradeConfig:  JSON.stringify(s.autoTradeConfig),
    riskSettings:     JSON.stringify(s.riskSettings),
    paperAccount:     JSON.stringify(s.paperAccount),
    panelLayouts:     JSON.stringify(s.panelLayouts),
    autoTradeRecords: JSON.stringify(s.autoTradeRecords.slice(0, 200)),
    // Strip runtime candle/signal data — only save chart identity + config
    activePanels:     JSON.stringify(s.activePanels.map(p => ({
      id:        p.id,
      symbol:    p.symbol,
      timeframe: p.timeframe,
      config:    p.config,
    }))),
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export type DbSyncStatus = 'idle' | 'loading' | 'ready' | 'error';

// Module-level guard — only run once even if hook is mounted in multiple components.
let _initialized = false;

export function useDbSync(): { status: DbSyncStatus; backend: string } {
  const status  = useTerminalStore(s => s.dbSyncStatus);
  const backend = useTerminalStore(s => s.dbBackend);
  // Settings keys changed in this tab since the last save, and keys the DB
  // snapshot already had when this tab loaded.
  const dirtyRef  = useRef(new Set<SettingsKey>());
  const dbKeysRef = useRef(new Set<string>());

  // ── Load snapshot on mount ────────────────────────────────────────────────

  useEffect(() => {
    if (_initialized) return;
    _initialized = true;

    let cancelled = false;
    useTerminalStore.getState().setDbSync('loading');

    (async () => {
      try {
        const [snapshot, info] = await Promise.all([
          loadSnapshot(),
          getPersistStatus().catch(() => ({ backend: 'sqlite', ok: true })),
        ]);
        if (cancelled) return;

        useTerminalStore.getState().setDbSync('loading', info.backend);

        const store = useTerminalStore.getState();
        const s     = snapshot.settings;
        dbKeysRef.current = new Set(Object.keys(s));

        // Scalar settings
        if (s['theme'])        store.setTheme(s['theme'] as Parameters<typeof store.setTheme>[0]);
        if (s['layout'])       store.setLayout(s['layout'] as Parameters<typeof store.setLayout>[0]);
        if (s['activeSymbol']) store.setActiveSymbol(s['activeSymbol']);
        if (s['uiScale'])      store.setUiScale(parseFloat(s['uiScale']));
        if (s['tradeMode'])    store.setTradeMode(s['tradeMode'] as Parameters<typeof store.setTradeMode>[0]);

        // JSON blobs — cast after parse since values are opaque strings
        const tryJson = (key: string, apply: (v: never) => void) => {
          try { if (s[key]) apply(JSON.parse(s[key]!) as never); } catch { /* ignore */ }
        };
        tryJson('settings',         store.updateSettings);
        tryJson('autoTradeConfig',  store.setAutoTradeConfig);
        tryJson('riskSettings',     store.setRiskSettings);
        tryJson('paperAccount',     store.setPaperAccount);
        tryJson('panelLayouts',     store.setPanelLayouts);
        tryJson('autoTradeRecords', store.setAutoTradeRecords);

        // activePanels — which charts are open (symbol, timeframe, config)
        // Candles/signals are always re-fetched live, so restore with empty arrays
        if (s['activePanels']) {
          try {
            const panels = JSON.parse(s['activePanels']!) as Array<{
              id: string; symbol: string; timeframe: string; config: unknown;
            }>;
            if (Array.isArray(panels) && panels.length > 0) {
              useTerminalStore.setState({
                activePanels: panels.map(p => ({
                  id:        p.id,
                  symbol:    p.symbol,
                  timeframe: (p.timeframe ?? '15m') as import('@arc/shared').TimeframeKey,
                  config:    (p.config as import('@arc/shared').UTBotConfig | undefined) ?? DEFAULT_UTBOT_CONFIG,
                  candles:   [],
                  signals:   [],
                })),
              });
            }
          } catch { /* ignore */ }
        }

        // Restore positions for any open autoTradeRecords so the PnL engine
        // can track unrealized gains/losses after a page reload.
        {
          const freshState  = useTerminalStore.getState();
          const leverage    = freshState.autoTradeConfig.leverage;
          const openRecords = freshState.autoTradeRecords.filter(r => r.status === 'open');
          for (const r of openRecords) {
            const pos: Position = {
              id:               generateId(),
              orderId:          r.id,
              symbol:           r.symbol,
              side:             r.side,
              size:             r.sizeUsd,
              entryPrice:       r.entryPrice,
              currentPrice:     r.entryPrice,
              unrealizedPnl:    0,
              unrealizedPnlPct: 0,
              leverage,
              mode:             'paper',
              exchange:         'paper',
              openedAt:         r.openedAt,
            };
            freshState.openPosition(pos);
          }
        }

        // Structured tables
        if (snapshot.orders.length > 0)
          store.setOrders(snapshot.orders.map(dbToOrder));

        if (snapshot.priceAlerts.length > 0)
          store.setPriceAlerts(snapshot.priceAlerts.map(dbToAlert));

        if (snapshot.equityCurve.length > 0)
          store.setEquityCurve(snapshot.equityCurve.map(dbToEquity));

        useTerminalStore.getState().setDbSync('ready');
      } catch (err) {
        if (!cancelled) {
          console.warn('[dbSync] DB unavailable — using localStorage only:', err);
          useTerminalStore.getState().setDbSync('error');
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // ── Write-back subscriptions (start only after initial load) ─────────────

  // Writes only the keys this tab changed. The DB is shared by every open tab
  // and device, so rewriting all keys on any change let a tab with stale state
  // (e.g. a watchlist from before a removal elsewhere) undo other tabs' edits.
  const syncSettings = useDebouncedCallback(() => {
    const dirty = dirtyRef.current;
    if (dirty.size === 0) return;
    const row = settingsRow(useTerminalStore.getState());
    const kv: Record<string, string> = {};
    for (const key of dirty) kv[key] = row[key];
    dirty.clear();
    saveSettings(kv).catch(console.warn);
  }, 2000);

  const markDirty = (key: SettingsKey) => () => {
    dirtyRef.current.add(key);
    syncSettings();
  };

  const syncOrders = useDebouncedCallback(() => {
    const orders = useTerminalStore.getState().orders;
    saveOrders(orders.slice(0, 500).map(orderToDb)).catch(console.warn);
  }, 2000);

  const syncAlerts = useDebouncedCallback(() => {
    const alerts = useTerminalStore.getState().priceAlerts;
    saveAlerts(alerts.map(alertToDb)).catch(console.warn);
  }, 1000);

  const syncEquity = useDebouncedCallback(() => {
    const curve = useTerminalStore.getState().equityCurve;
    saveEquityPoints(curve.slice(-50).map(equityToDb)).catch(console.warn);
  }, 5000);

  useEffect(() => {
    if (status !== 'ready') return;

    // Write any keys the DB doesn't have yet (first run, or keys added since).
    // Runs once when DB becomes ready; debounce handles subsequent changes.
    for (const key of SETTINGS_KEYS) if (!dbKeysRef.current.has(key)) dirtyRef.current.add(key);
    syncSettings();

    const unsubs = [
      useTerminalStore.subscribe(s => s.theme,            markDirty('theme')),
      useTerminalStore.subscribe(s => s.layout,           markDirty('layout')),
      useTerminalStore.subscribe(s => s.activeSymbol,     markDirty('activeSymbol')),
      useTerminalStore.subscribe(s => s.uiScale,          markDirty('uiScale')),
      useTerminalStore.subscribe(s => s.tradeMode,        markDirty('tradeMode')),
      useTerminalStore.subscribe(s => s.settings,         markDirty('settings')),
      useTerminalStore.subscribe(s => s.autoTradeConfig,  markDirty('autoTradeConfig')),
      useTerminalStore.subscribe(s => s.riskSettings,     markDirty('riskSettings')),
      useTerminalStore.subscribe(s => s.paperAccount,     markDirty('paperAccount')),
      useTerminalStore.subscribe(s => s.panelLayouts,     markDirty('panelLayouts')),
      useTerminalStore.subscribe(s => s.autoTradeRecords, markDirty('autoTradeRecords')),
      useTerminalStore.subscribe(s => s.activePanels,     markDirty('activePanels')),
      useTerminalStore.subscribe(s => s.orders,           syncOrders),
      useTerminalStore.subscribe(s => s.priceAlerts,      syncAlerts),
      useTerminalStore.subscribe(s => s.equityCurve,      syncEquity),
    ];

    return () => unsubs.forEach(u => u());
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, backend };
}
